import {
  Briefcase,
  Camera,
  Fingerprint,
  IdCard,
  Paperclip,
  Plus,
  Save,
  Shield,
  Trash2,
  Upload,
  User,
  X,
} from 'lucide-react';
import {
  FOOD_ALLOWANCE_TYPES,
  IDENTITY_VAULT_FIELDS,
  MODAL_CERT_OPTIONS,
  OVERTIME_RULES,
  SPECIAL_ALLOWANCE_TYPES,
} from './mockDriverErpData';

const inputClass =
  'w-full rounded-lg border border-logi-grid bg-white px-3 py-2 text-sm text-logi-text placeholder-logi-subtle outline-none transition focus:border-violet-500/50 focus:ring-1 focus:ring-logi-accent/30';

const labelClass = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-logi-text0';

const sectionClass = 'rounded-xl border border-logi-grid bg-white p-4';

export default function DriverProfileModal({
  isOpen,
  isEditing,
  form,
  onFieldChange,
  onToggleCert,
  onProfilePhotoUpload,
  onIdentityDocUpload,
  onCertDocUpload,
  onContractFieldChange,
  onAddSpecialAllowance,
  onRemoveSpecialAllowance,
  onUpdateSpecialAllowance,
  onSave,
  onClose,
}) {
  if (!isOpen) return null;

  const contract = form.contractDetails || {};
  const foodAmountHint =
    contract.foodAllowanceType === 'Per Duty Day'
      ? 'Amount per duty day (AED)'
      : 'Monthly amount (AED)';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-[fadeSlideIn_0.2s_ease-out]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="driver-modal-title"
    >
      <button
        type="button"
        aria-label="Close modal backdrop"
        className="absolute inset-0 bg-logi-silver/80 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-logi-grid bg-white shadow-2xl shadow-violet-500/10 ring-1 ring-violet-500/20">
        <div className="flex items-start justify-between border-b border-logi-grid bg-gradient-to-r from-white via-logi-silver to-logi-silver px-5 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-logi-accent/80">
              Driver Registry · Biometric Vault
            </p>
            <h3 id="driver-modal-title" className="text-lg font-bold text-logi-text">
              {isEditing ? 'Edit Driver Profile' : 'Register New Driver'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-logi-grid bg-white p-2 text-logi-muted transition hover:border-red-500/40 hover:bg-red-50 hover:text-red-700"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={onSave} className="flex-1 space-y-4 overflow-y-auto p-5">
          <div className={`${sectionClass} flex flex-col items-center`}>
            <div className="mb-3 flex items-center gap-2 self-start">
              <Fingerprint className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
              <h4 className="text-xs font-bold uppercase tracking-wider text-logi-muted">
                Biometric Profile Photo
              </h4>
            </div>

            <label className="group relative cursor-pointer">
              {form.profilePhoto?.previewUrl ? (
                <img
                  src={form.profilePhoto.previewUrl}
                  alt="Driver preview"
                  className="h-28 w-28 rounded-full object-cover ring-4 ring-logi-accent/30 transition group-hover:ring-violet-400/50"
                />
              ) : (
                <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full border-2 border-dashed border-logi-grid bg-white transition group-hover:border-violet-500/40 group-hover:bg-logi-accent/10">
                  <Camera className="h-8 w-8 text-logi-text0 group-hover:text-logi-accent" />
                </div>
              )}
              <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-white shadow-lg ring-2 ring-white">
                <Upload className="h-3.5 w-3.5" />
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onProfilePhotoUpload(file);
                  e.target.value = '';
                }}
              />
            </label>
            <p className="mt-3 text-xs font-semibold text-logi-muted">Upload Driver Photo</p>
            {form.profilePhoto && (
              <p className="mt-1 max-w-[220px] truncate font-mono text-[10px] text-logi-accent/80">
                {form.profilePhoto.fileName}
              </p>
            )}
          </div>

          <div className={sectionClass}>
            <div className="mb-3 flex items-center gap-2">
              <User className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
              <h4 className="text-xs font-bold uppercase tracking-wider text-logi-muted">
                Personal Details
              </h4>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Driver Name</label>
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => onFieldChange('name', e.target.value)}
                  placeholder="Full legal name"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Nationality</label>
                <input
                  className={inputClass}
                  value={form.nationality}
                  onChange={(e) => onFieldChange('nationality', e.target.value)}
                  placeholder="e.g. UAE, India, Pakistan"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Mobile</label>
                <input
                  className={inputClass}
                  value={form.mobile}
                  onChange={(e) => onFieldChange('mobile', e.target.value)}
                  placeholder="+971 50 XXX XXXX"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Emirates ID</label>
                <input
                  className={inputClass}
                  value={form.emiratesId}
                  onChange={(e) => onFieldChange('emiratesId', e.target.value)}
                  placeholder="784-XXXX-XXXXXXX-X"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Passport Number</label>
                <input
                  className={inputClass}
                  value={form.passport}
                  onChange={(e) => onFieldChange('passport', e.target.value)}
                  placeholder="Passport number"
                />
              </div>
              <div>
                <label className={labelClass}>Visa Status</label>
                <input
                  className={inputClass}
                  value={form.visa}
                  onChange={(e) => onFieldChange('visa', e.target.value)}
                  placeholder="Visa status / expiry"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Driving License Number</label>
                <input
                  className={inputClass}
                  value={form.drivingLicenseNumber}
                  onChange={(e) => onFieldChange('drivingLicenseNumber', e.target.value)}
                  placeholder="HL-AD-XXXXXX"
                />
              </div>
            </div>
          </div>

          <div className={`${sectionClass} ring-1 ring-cyan-500/15`}>
            <div className="mb-3 flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
              <h4 className="text-xs font-bold uppercase tracking-wider text-logi-muted">
                Employment &amp; Contract Rules
              </h4>
              <span className="rounded bg-logi-accent/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-logi-accent ring-1 ring-logi-accent/30">
                Required
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Basic Salary (AED)</label>
                <input
                  type="number"
                  min="1"
                  step="100"
                  className={inputClass}
                  value={contract.basicSalary ?? ''}
                  onChange={(e) =>
                    onContractFieldChange('basicSalary', Number(e.target.value) || 0)
                  }
                  placeholder="e.g. 3500"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Overtime Rule</label>
                <select
                  className={inputClass}
                  value={contract.overtimeRule || OVERTIME_RULES[0]}
                  onChange={(e) => onContractFieldChange('overtimeRule', e.target.value)}
                  required
                >
                  {OVERTIME_RULES.map((rule) => (
                    <option key={rule} value={rule}>
                      {rule}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Food Allowance Calculation</label>
                <select
                  className={inputClass}
                  value={contract.foodAllowanceType || FOOD_ALLOWANCE_TYPES[0]}
                  onChange={(e) => onContractFieldChange('foodAllowanceType', e.target.value)}
                  required
                >
                  {FOOD_ALLOWANCE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Food Allowance Amount (AED)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputClass}
                  value={contract.foodAllowanceAmount ?? ''}
                  onChange={(e) =>
                    onContractFieldChange('foodAllowanceAmount', Number(e.target.value) || 0)
                  }
                  placeholder={
                    contract.foodAllowanceType === 'Per Duty Day' ? 'e.g. 20 per day' : 'e.g. 500'
                  }
                  required
                />
                <p className="mt-1 text-[9px] text-logi-text0">{foodAmountHint}</p>
              </div>
            </div>

            <div className="mt-4 border-t border-logi-grid/80 pt-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
                  Special Allowances
                </p>
                <button
                  type="button"
                  onClick={onAddSpecialAllowance}
                  className="inline-flex items-center gap-1 rounded-md border border-logi-accent/30 bg-logi-accent/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-logi-accent transition hover:bg-logi-accent/10"
                >
                  <Plus className="h-3 w-3" />
                  Add Allowance
                </button>
              </div>

              {(contract.specialAllowances || []).length === 0 ? (
                <p className="rounded-lg border border-dashed border-logi-grid bg-white/20 px-3 py-2 text-[10px] text-logi-text0">
                  No special allowances — optional ADR/Hazmat bonus, night premium, etc.
                </p>
              ) : (
                <div className="space-y-2">
                  {(contract.specialAllowances || []).map((item, index) => (
                    <div
                      key={`allowance-${index}`}
                      className="flex flex-wrap items-end gap-2 rounded-lg border border-logi-grid/80 bg-white p-2.5"
                    >
                      <div className="min-w-[160px] flex-1">
                        <label className={labelClass}>Allowance Type</label>
                        <input
                          list={`allowance-types-${index}`}
                          className={inputClass}
                          value={item.type}
                          onChange={(e) => onUpdateSpecialAllowance(index, 'type', e.target.value)}
                          placeholder="e.g. ADR/Hazmat Bonus"
                        />
                        <datalist id={`allowance-types-${index}`}>
                          {SPECIAL_ALLOWANCE_TYPES.map((type) => (
                            <option key={type} value={type} />
                          ))}
                        </datalist>
                      </div>
                      <div className="w-28">
                        <label className={labelClass}>Amount (AED)</label>
                        <input
                          type="number"
                          min="0"
                          step="50"
                          className={inputClass}
                          value={item.amount ?? ''}
                          onChange={(e) =>
                            onUpdateSpecialAllowance(index, 'amount', Number(e.target.value) || 0)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveSpecialAllowance(index)}
                        className="mb-0.5 rounded-md border border-red-200 bg-red-50 p-2 text-red-700 transition hover:bg-red-500/15"
                        aria-label="Remove allowance"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={sectionClass}>
            <div className="mb-3 flex items-center gap-2">
              <IdCard className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
              <h4 className="text-xs font-bold uppercase tracking-wider text-logi-muted">
                Identity Document Vault
              </h4>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {IDENTITY_VAULT_FIELDS.map(({ key, label }) => {
                const attachment = form.identityVault?.[key];
                return (
                  <div
                    key={key}
                    className="rounded-lg border border-logi-grid/80 bg-white p-3"
                  >
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-logi-muted">
                      {label}
                    </p>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-logi-grid bg-white px-2.5 py-1.5 text-[10px] font-semibold text-logi-muted transition hover:border-violet-500/40 hover:text-violet-300">
                      <Paperclip className="h-3 w-3" />
                      Select File
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onIdentityDocUpload(key, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {attachment && (
                      <span
                        className="mt-2 flex max-w-full items-center gap-1 truncate rounded-md bg-logi-accent/10 px-2 py-0.5 font-mono text-[9px] font-semibold text-violet-300 ring-1 ring-violet-500/25"
                        title={attachment.mockUrl}
                      >
                        <Paperclip className="h-2.5 w-2.5 flex-shrink-0" />
                        {attachment.fileName}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={sectionClass}>
            <div className="mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
              <h4 className="text-xs font-bold uppercase tracking-wider text-logi-muted">
                Certifications &amp; Certificate Files
              </h4>
              <span className="text-[10px] text-logi-text0">Multi-select + upload</span>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {MODAL_CERT_OPTIONS.map(({ key, label, shortLabel }) => {
                const checked = form.selectedCerts.includes(key);
                const certFile = form.certAttachments?.[key];
                return (
                  <div
                    key={key}
                    className={`rounded-lg border px-3 py-2.5 transition-all ${
                      checked
                        ? 'border-violet-500/40 bg-logi-accent/10 ring-1 ring-logi-accent/30'
                        : 'border-logi-grid bg-white'
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex flex-1 cursor-pointer items-center gap-3 min-w-[180px]">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleCert(key)}
                          className="h-4 w-4 rounded border-logi-grid bg-white text-violet-500 focus:ring-violet-500/40 focus:ring-offset-0"
                        />
                        <span className={`text-xs font-medium ${checked ? 'text-violet-200' : 'text-logi-muted'}`}>
                          <span className="mr-1.5 font-mono text-[10px] font-bold text-logi-accent/70">
                            {shortLabel}
                          </span>
                          {label}
                        </span>
                      </label>

                      {checked && (
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-emerald-700 transition hover:bg-emerald-500/15">
                            <Upload className="h-3 w-3" />
                            Upload Cert
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) onCertDocUpload(key, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          {certFile && (
                            <span
                              className="inline-flex max-w-[140px] items-center gap-1 truncate rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-[9px] font-semibold text-emerald-700 ring-1 ring-emerald-500/25"
                              title={certFile.mockUrl}
                            >
                              <Paperclip className="h-2.5 w-2.5 flex-shrink-0" />
                              {certFile.fileName}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-2 border-t border-logi-grid bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-logi-grid px-4 py-2 text-sm font-semibold text-logi-muted transition hover:bg-white hover:text-logi-text"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-violet-500 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-500/20 transition hover:from-violet-500 hover:to-violet-400"
          >
            <Save className="h-4 w-4" />
            {isEditing ? 'Save Changes' : 'Register Driver'}
          </button>
        </div>
      </div>
    </div>
  );
}
